/**
 * Error reporting utilities.
 * - Logs to the `error_logs` table via a server function (best-effort).
 * - Forwards to Sentry (@sentry/react) on the client when VITE_SENTRY_DSN is set.
 *
 * TODO:
 * - Sentry release tracking (link build hash → uploaded source maps) once
 *   production rollout starts.
 * - Server-side capture for TanStack server functions via @sentry/cloudflare
 *   (worker entry wrapping). Edge Functions are out of scope (Supabase Logs).
 */
import * as Sentry from "@sentry/react";
import { logClientError } from "@/lib/error-logs.functions";
import { supabase } from "@/integrations/supabase/client";

let initialized = false;
let sentryEnabled = false;

// DSN is a publishable identifier (safe to ship in the client bundle).
// Override via VITE_SENTRY_DSN if needed.
const SENTRY_DSN =
  (import.meta.env.VITE_SENTRY_DSN as string | undefined) ||
  "https://04c9e4798d11b66aa62dbaa8dff94ead@o4511382261202944.ingest.us.sentry.io/4511382315597824";
const SENTRY_ENV =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
  (import.meta.env.DEV ? "development" : "production");

export function initErrorReporting() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  if (SENTRY_DSN) {
    try {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: SENTRY_ENV,
        // Conservative defaults — tune after production traffic arrives.
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        sendDefaultPii: false,
      });
      sentryEnabled = true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Sentry] init failed:", e);
    }
  }

  // Catch unhandled runtime errors
  window.addEventListener("error", (event) => {
    void reportError(event.error ?? new Error(event.message), {
      route: window.location.pathname,
      source: "window.error",
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const err =
      reason instanceof Error ? reason : new Error(String(reason ?? "Unhandled rejection"));
    void reportError(err, {
      route: window.location.pathname,
      source: "unhandledrejection",
    });
  });
}

export async function reportError(
  error: unknown,
  context: { route?: string; source?: string } = {},
) {
  try {
    const err =
      error instanceof Error ? error : new Error(typeof error === "string" ? error : "Unknown error");

    // Always log to console for dev visibility
    // eslint-disable-next-line no-console
    console.error("[reportError]", err, context);

    if (typeof window === "undefined") return;

    // Forward to Sentry when enabled.
    if (sentryEnabled) {
      try {
        Sentry.captureException(err, {
          tags: {
            source: context.source ?? "unknown",
            route: context.route ?? window.location.pathname,
          },
        });
      } catch {
        // never throw from the reporter
      }
    }

    let userId: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user?.id ?? null;
    } catch {
      // ignore
    }

    if (sentryEnabled && userId) {
      try {
        Sentry.setUser({ id: userId });
      } catch {
        // ignore
      }
    }

    // Skip server-side logging when unauthenticated — the endpoint requires
    // auth (user_id is derived server-side from the session, not client input).
    if (!userId) {
      return;
    }

    await logClientError({
      data: {
        message: err.message?.slice(0, 2000) || "Unknown error",
        stack: err.stack?.slice(0, 8000) ?? null,
        route: context.route ?? window.location.pathname,
        user_agent: navigator.userAgent?.slice(0, 500) ?? null,
      },
    }).catch(() => {
      /* swallow */
    });

  } catch {
    // never throw from the reporter
  }
}

/** Generate a short error ID for display to the user */
export function generateErrorId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${ts}-${rand}`.toUpperCase();
}
